<template>
    <a-modal
        :visible="visible"
        :title="'YouTube Ayarları - ' + widget.title"
        width="600px"
        :footer="null"
        @cancel="$emit('close')"
        class="youtube-settings-modal"
        :destroyOnClose="true"
    >
        <a-form :form="form" layout="vertical">
            <!-- Video ID veya URL -->
            <a-form-item :label="$t('table.videoUrlOrId')">
                <a-input
                    v-model="videoInput"
                    placeholder="https://www.youtube.com/watch?v=VIDEO_ID veya VIDEO_ID"
                    @change="handleVideoInputChange"
                />
                <div v-if="videoError" class="error-message">{{ videoError }}</div>
            </a-form-item>

            <!-- Oynatma Ayarları -->
            <a-form-item :label="$t('table.playbackSettings')">
                <div class="playback-settings">
                    <a-checkbox v-model="previewSettings.autoplay">{{ $t('table.autoplay') }}</a-checkbox>
                    <a-checkbox v-model="previewSettings.controls">{{ $t('table.showControls') }}</a-checkbox>
                    <a-checkbox v-model="previewSettings.showInfo">{{ $t('table.showVideoInfo') }}</a-checkbox>
                    <a-checkbox v-model="previewSettings.loop">{{ $t('table.loopPlay') }}</a-checkbox>
                    <a-checkbox v-model="previewSettings.mute">{{ $t('table.startMuted') }}</a-checkbox>
                </div>
            </a-form-item>

            <!-- Başlangıç Zamanı -->
            <a-form-item :label="$t('table.startTimeSeconds')">
                <a-input-number
                    v-model="previewSettings.startAt"
                    :min="0"
                    :max="3600"
                    style="width: 100%"
                />
            </a-form-item>

            <!-- Önizleme -->
            <a-form-item :label="$t('table.preview')">
                <div class="preview-container">
                    <iframe
                        v-if="previewSettings.videoId"
                        :src="previewUrl"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                        style="width: 100%; height: 315px"
                    ></iframe>
                    <div v-else class="preview-placeholder">
                        <i class="material-icons">play_circle_outline</i>
                        <span>{{ $t('table.enterVideoUrlOrId') }}</span>
                    </div>
                </div>
            </a-form-item>

            <!-- Kaydet/İptal Butonları -->
            <div class="form-actions">
                <a-button @click="$emit('close')">{{ $t('table.cancel') }}</a-button>
                <a-button
                    type="primary"
                    @click="handleSave"
                    :loading="saving"
                    :disabled="!previewSettings.videoId"
                >
                    {{ $t('table.save') }}
                </a-button>
            </div>
        </a-form>
    </a-modal>
</template>

<script>
import Vue from "vue";
import _ from "lodash";

export default Vue.extend({
    name: "YoutubeSettingsModal",
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        widget: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            form: this.$form.createForm(this),
            previewSettings: _.cloneDeep(this.widget.settings || {
                videoId: "",
                autoplay: false,
                controls: true,
                showInfo: true,
                loop: false,
                mute: false,
                startAt: 0,
            }),
            videoInput: "",
            videoError: null,
            saving: false,
        };
    },
    computed: {
        previewUrl() {
            if (!this.previewSettings.videoId) return "";
            const params = new URLSearchParams({
                autoplay: this.previewSettings.autoplay ? "1" : "0",
                controls: this.previewSettings.controls ? "1" : "0",
                showinfo: this.previewSettings.showInfo ? "1" : "0",
                loop: this.previewSettings.loop ? "1" : "0",
                mute: this.previewSettings.mute ? "1" : "0",
                start: this.previewSettings.startAt || 0,
            });
            return `https://www.youtube.com/embed/${this.previewSettings.videoId}?${params.toString()}`;
        },
    },
    created() {
        if (this.widget.settings?.videoId) {
            this.videoInput = this.widget.settings.videoId;
        }
    },
    methods: {
        extractVideoId(input) {
            // YouTube URL'den video ID'sini çıkar
            const patterns = [
                /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
                /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([^?]+)/,
                /^https?:\/\/youtu\.be\/([^?]+)/,
                /^([a-zA-Z0-9_-]{11})$/, // Direkt video ID
            ];

            for (const pattern of patterns) {
                const match = input.match(pattern);
                if (match) {
                    return match[1];
                }
            }

            return null;
        },
        handleVideoInputChange() {
            const videoId = this.extractVideoId(this.videoInput.trim());
            if (videoId) {
                this.previewSettings.videoId = videoId;
                this.videoError = null;
            } else {
                this.videoError = "Geçerli bir YouTube URL'si veya video ID'si giriniz";
                this.previewSettings.videoId = "";
            }
        },
        async handleSave() {
            if (!this.previewSettings.videoId) {
                this.$message.error("Lütfen geçerli bir video ID giriniz");
                return;
            }

            try {
                this.saving = true;
                await this.$emit("save", this.previewSettings);
            } catch (error) {
                console.error("Ayarlar kaydedilirken hata:", error);
                this.$message.error("Ayarlar kaydedilirken bir hata oluştu");
            } finally {
                this.saving = false;
            }
        },
    },
});
</script>

<style lang="scss">
.youtube-settings-modal {
    .playback-settings {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .preview-container {
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        overflow: hidden;

        .preview-placeholder {
            height: 315px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: #fafafa;
            color: rgba(0, 0, 0, 0.45);
            gap: 8px;

            .material-icons {
                font-size: 48px;
            }
        }
    }

    .error-message {
        color: #ff4d4f;
        font-size: 12px;
        margin-top: 4px;
    }

    .form-actions {
        margin-top: 24px;
        display: flex;
        justify-content: flex-end;
        gap: 8px;
    }
}
</style> 